// =============================================================================
//  Copyright   : 2018 semantics GmbH
//  Date        : $Date: 2022-08-19 13:56:49 +0200 (Fri, 19 Aug 2022) $
//  Author      : Dirk Rothe
//  Worker      : $Author: d.rothe $
//  Revision    : $Rev: 128693 $
//  Purpose     : normalize raw or compressed db query results
// =============================================================================
// ---Third-party imports
import memoizeOne from 'memoize-one'

// ---Framework imports
import {get, entries, size, dict, mapItems, filterItems, isEqual, flatten, values} from 'ldash'
import log from 'uix/logging'

// ---Globals
// we rename OPR_Tree fields and some customs
// other tables fields are only lowercased
let transSchema = (k, v) => {
  switch (k) {
    case 'OT_ID':
      return ['otid', v]
    case 'OT_OD_ID':
      return ['odid', v]
    case 'OT_OT_LINK':
      return ['otlink', v]
    case 'OT_LINK_OD_ID':
      return ['odlink', v]
    case 'OT_OT_PARENT':
      return ['otparent', v]
    case 'OT_RCV_DATATYPE':
      return ['datatype', v]
    case 'OT_RCV_TYPE':
      return ['subtype', v]
    case 'OT_CAPTION':
      return ['caption', v]
    case 'OT_INSERTTS':
      return ['insertts', v]
    case 'OT_DELSTATE':
      return ['delstate', v]
    case 'OT_PATH':
      return [
        'otpath',
        v
          .slice(1, -1)
          .split('||')
          .map(e => parseInt(e)),
      ]
    case 'OT_SORT':
      return ['sort', v]
    case 'CHILDCOUNT':
      return ['count', v]
    default:
      return [k.toLowerCase(), v]
  }
}

export let normalize = (rows, meta) => {
  if (!meta) {
    meta = {format: 'raw'}
  }
  if (meta.format == 'raw') {
    return rows.map(r => dict(mapItems(r, (k, v) => transSchema(k, v))))
  } else if (meta.format == 'raw_lower') {
    return rows.map(r => dict(mapItems(r, (k, v) => transSchema(k.toUpperCase(), v))))
  } else if (meta.format == 'compressed') {
    let fields = meta.fields
    return rows.map(r => dict(fields.slice(0, r.length).map((k, i) => transSchema(k, r[i]))))
  } else {
    log.error('unknown format: %o', meta.format)
    return []
  }
}

export const normalizeByDatatype = byDatatype =>
  flatten(values(byDatatype).map(({rows, meta}) => normalize(rows, meta)))

let _memoized = ({rows, format}) => normalize(rows, {format})
// XXX: supports only one argument - maybe change the signature of normalize() as well
export let normalizeMemo = memoizeOne(
  _memoized,
  isEqual
  //~ ({rows, format}, old) => rows === old.rows && format === old.format
)
